import {Link} from '@wandb/weave/common/util/links';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useEntityIntegrationsQuery} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {type ApolloError} from '../../../../state/graphql/apolloWithAbort';
import * as urls from '../../../../util/urls';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';
import {useSlackIntegrationsFromEntity} from '../../../SlackIntegration/hooks';
import {type SlackIntegration} from '../../../SlackIntegration/utils';
import {InstrumentedLoader} from '../../../utility/InstrumentedLoader';
import {useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {useEventStore} from './EventStore';
import {ErrorText} from './shared';

const formatNotificationOptionLabel = (
  notificationOption: NotificationOptionType | null
) => {
  if (notificationOption == null) {
    return;
  }
  const {label} = notificationOption;
  return (
    <S.SelectOption>
      <S.SelectOptionLabelText title={label}>{label}</S.SelectOptionLabelText>
    </S.SelectOption>
  );
};

export type NotificationOptionType = {
  label: string;
  value: SlackIntegration;
};

export const NotificationSection = ({
  integrations,
  slackIntegrationsLoading,
  slackIntegrationsError,
}: {
  integrations: SlackIntegration[];
  slackIntegrationsLoading: boolean;
  slackIntegrationsError: ApolloError | undefined;
}) => {
  const {updateNotificationConfig, entityName} = useActionConfigStore(
    useShallow(s => s)
  );
  const entryPoint = useEventStore(state => state.entryPoint);
  const {teamSlackIntegration} = useActionConfigStore(
    useShallow(s => s.notification)
  );

  // Check if still loading, or if no slack integrations are registered
  if (slackIntegrationsLoading) {
    return (
      <InstrumentedLoader active name="create-trigger-drawer-notification" />
    );
  }
  if (slackIntegrationsError) {
    return <ErrorText>Error loading slack integrations</ErrorText>;
  }

  const notificationOptions: NotificationOptionType[] = integrations.map(w => {
    return {
      value: w,
      label: w.channelName,
    };
  });

  const currentNotificationOption =
    notificationOptions.find(w => w.value.id === teamSlackIntegration?.id) ??
    null; // note that null tells react-select to show the placeholder, undefined will not

  return (
    <>
      <Tailwind>
        <div className="mb-4 mt-16 font-semibold">Slack channel</div>

        {integrations.length === 0 && (
          <Banner variant="announcement">
            <BannerFlexWrapper>
              <BannerTextWrapper>
                <Icon name="lightbulb-info" />
                <div>
                  <span className="font-semibold">No Slack channels.</span> A
                  Slack integration must first be set up in team settings.
                </div>
              </BannerTextWrapper>
              <Link
                className="whitespace-nowrap font-semibold"
                to={urls.teamSettings(entityName ?? '')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  Analytics.track('Automation Creation', {
                    type: 'link_click',
                    entryPoint,
                    link: 'slack_channel_team_settings',
                  });
                }}>
                Team settings
              </Link>
            </BannerFlexWrapper>
          </Banner>
        )}
        {integrations.length > 0 && (
          <>
            <div className="mb-8 text-sm text-moon-600 dark:text-wb-text-secondary">
              Available slack channels can be managed in{' '}
              <Link
                to={urls.teamSettings(entityName ?? '')}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-wb-text-link hover:text-wb-text-link-hover"
                onClick={() => {
                  Analytics.track('Automation Creation', {
                    type: 'link_click',
                    entryPoint,
                    link: 'slack_channel_team_settings',
                  });
                }}>
                team settings
              </Link>
            </div>
            <Select<NotificationOptionType | null>
              options={notificationOptions}
              placeholder="Select a Slack channel..."
              value={currentNotificationOption}
              onChange={(d: NotificationOptionType | null) => {
                if (d) {
                  updateNotificationConfig({teamSlackIntegration: d.value});
                }
              }}
              autoFocus={true}
              formatOptionLabel={formatNotificationOptionLabel}
            />
          </>
        )}
      </Tailwind>
    </>
  );
};

export const useSlackIntegrations = (entityName: string | undefined) => {
  const entityIntegrations = useEntityIntegrationsQuery({
    variables: {
      name: entityName ?? '' /* ?'' valid because of skip below */,
    },
    skip: entityName == null,
  });
  const slackIntegrations = useSlackIntegrationsFromEntity(
    entityIntegrations?.data?.entity
  );
  return {
    slackIntegrations,
    loading: entityIntegrations.loading,
    error: entityIntegrations.error,
  };
};
